import {
  faArrowRight,
  faCalculator,
  faChartLine,
} from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useEffect, useRef, type MouseEvent } from 'react'
import { useStore } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Icon, isModifiedClick, Link, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import {
  FETCH_CURRENT_USER_KEY,
  fetchCurrentUserDetailsIfNeeded,
} from '@/actions/userActions'
import {
  FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS,
  fetchUserCatchUpBookkeepingStatus,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import {
  FETCH_USER_ONBOARDING_STEPS_KEY,
  fetchUserOnboardingStepsIfNeeded,
} from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { getFetchError } from '@/reducers/fetch'
import { useAppDispatch, type ReduxState } from '@/utils/typeHelpers'
import { closePanel } from '../adviser-chat.slice'
import { useAdviserAnalytics } from '../analytics'
import { PAGE_PATHS } from '../lib/constants'
import { WidgetCard } from './widgets/WidgetCard'

const NEXT_STEPS = [
  {
    icon: faChartLine,
    title: 'See your Profit & Loss and reports',
    description: 'Income and expenses for every month, ready to open now.',
    path: PAGE_PATHS.profitLoss,
  },
  {
    icon: faCalculator,
    title: 'Get your quarterly estimate',
    description:
      'What to set aside for taxes this quarter, worked out from your real numbers.',
    path: PAGE_PATHS.quarterlyTaxes,
  },
] as const

const RELOAD_KEYS = [
  FETCH_CURRENT_USER_KEY,
  FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS,
  FETCH_USER_ONBOARDING_STEPS_KEY,
]

export const WhatsNextCard = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const store = useStore<ReduxState>()
  const { trackNextStepClicked } = useAdviserAnalytics()
  const refreshes = useRef<Promise<unknown>>(Promise.resolve())

  // Finishing catch-up changes the user, its catch-up status and its onboarding
  // steps on the server, and the routes read all three from what was loaded.
  const reload = useCallback(() => {
    refreshes.current = Promise.allSettled([
      dispatch(fetchCurrentUserDetailsIfNeeded(true)),
      dispatch(fetchUserCatchUpBookkeepingStatus()),
      dispatch(
        fetchUserOnboardingStepsIfNeeded({ alwaysFetch: true, expanded: true })
      ),
    ])
  }, [dispatch])

  useEffect(() => reload(), [reload])

  // A failed reload resolves rather than throws, so routing now would use the
  // stale state; stay on the card and retry instead.
  const goTo = async (path: string): Promise<void> => {
    await refreshes.current
    if (RELOAD_KEYS.some((key) => getFetchError(store.getState(), key))) {
      reload()
      return
    }
    navigate(path)
    dispatch(closePanel())
  }

  const handleStepClick = (
    e: MouseEvent<HTMLAnchorElement>,
    path: string
  ): void => {
    trackNextStepClicked(path)
    if (isModifiedClick(e)) return
    e.preventDefault()
    void goTo(path)
  }

  const handleDashboard = (): void => {
    trackNextStepClicked(PAGE_PATHS.dashboard)
    void goTo(PAGE_PATHS.dashboard)
  }

  return (
    <div className="my-2 flex flex-col items-start gap-3">
      <div className="w-full">
        <WidgetCard>
          <Text variant={TypographyStyles.LABEL_MD}>What&rsquo;s next</Text>
          <div className="flex flex-col">
            {NEXT_STEPS.map(({ icon, title, description, path }) => (
              <Link
                key={path}
                href={path}
                variant="noUnderline"
                onClick={(e) => handleStepClick(e, path)}
                className="flex items-center gap-3 border-b border-neutral-100 py-3 last:border-b-0"
              >
                <Icon
                  icon={icon}
                  color="text-neutral-700"
                  className="text-[14px]"
                  backgroundClassName="mt-0.5 self-start"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <Text variant={TypographyStyles.LABEL_SM}>{title}</Text>
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    color={TextColors.MUTED}
                  >
                    {description}
                  </Text>
                </div>
                <Icon
                  icon={faArrowRight}
                  color="text-neutral-500"
                  className="text-[14px]"
                />
              </Link>
            ))}
          </div>
        </WidgetCard>
      </div>
      <Button variant="primary" size="sm" onClick={handleDashboard}>
        Go to dashboard
      </Button>
    </div>
  )
}
